import SeoHead from '../components/SeoHead'
import { SITE } from '../lib/site'

// Public direct-download of W.Sign for Windows (Velopack setup), single-sourced from SITE.downloadUrl.
// Hosted on our own domain because the GitHub repo is private; the URL always serves the latest, and
// an installed copy self-updates from the same folder's releases.win.json.
//
// Replaces the standalone "W.Sign Köprüsü" agent download: the agent has been folded back into the
// single Desktop app, which is tray-first and hosts the very same loopback bridge. Deliberately no
// size figure here — the old copy claimed "~16 MB" long after the real package had grown to 80 MB+.

export default function DownloadPage() {
  return (
    <>
      <SeoHead
        title="W.Sign indir — Windows e-imza uygulaması"
        description="W.Sign Windows uygulamasını indirin: CAdES, XAdES ve PAdES imzalama, imza doğrulama, kart/token yönetimi ve tarayıcıdan gelen imza isteklerini karşılayan tepsi köprüsü. Kurulumdan sonra kendini günceller."
        path="/download"
      />
      <section className="container-page py-12 md:py-16">
        <h1 className="text-3xl md:text-4xl font-semibold">İndir</h1>
        <p className="mt-2 text-[var(--color-fg-muted)] max-w-2xl">
          <strong>W.Sign</strong>, Windows için e-imza uygulamasıdır. Belgelerinizi doğrudan
          imzalayıp doğrulayabilir, aynı zamanda web portalında USB token'ınızla nitelikli imza
          atabilirsiniz. Kurulumdan sonra sistem tepsisinde sessizce çalışır.
        </p>

        <div className="mt-8 max-w-md">
          <DownloadCard
            title="W.Sign"
            badge="Windows"
            sub="Windows x86 · kurulum sonrası kendini günceller"
            primaryCommand="Kurun, hemen imzalamaya başlayın"
            link={SITE.downloadUrl}
            linkLabel="İndir (Windows kurulum)"
          />
        </div>

        <div className="card mt-6">
          <h3 className="font-semibold">Neler yapabilir?</h3>
          <ul className="mt-2 space-y-1 text-sm text-[var(--color-fg-muted)] list-disc pl-5">
            <li>CAdES, XAdES ve PAdES imzalama — tek dosya ya da toplu.</li>
            <li>İmza doğrulama: imzalayan, zaman damgası, sertifika zinciri ve iptal durumu.</li>
            <li>Web portalı köprüsü: tarayıcıdan gelen imza isteğini kartınızla karşılar.</li>
            <li>Kart/token yönetimi ve imza geçmişi.</li>
          </ul>
        </div>

        <div className="card mt-6 border-amber-200 bg-amber-50">
          <h3 className="font-semibold text-amber-900">Windows güvenlik uyarısı hakkında</h3>
          <p className="mt-2 text-sm text-amber-800">
            İmzalama sertifikamız temin sürecindedir; bu nedenle Windows SmartScreen ilk kurulumda
            "Bilinmeyen yayıncı" uyarısı gösterebilir. <strong>"Daha fazla bilgi"</strong> →{' '}
            <strong>"Yine de çalıştır"</strong> adımlarını takip ederek kurulumu tamamlayın.
          </p>
        </div>

        <div className="card mt-6 border-amber-200 bg-amber-50">
          <h3 className="font-semibold text-amber-900">32-bit token sürücüleri</h3>
          <p className="mt-2 text-sm text-amber-800">
            AKİS ve çoğu SafeNet sürücüsü sadece <strong>32-bit PKCS#11</strong> kütüphanesi sağlar.
            W.Sign kurulum paketi zaten <code>win-x86</code> olarak derlenir; ek adım gerekmez.
          </p>
        </div>

        <div className="card mt-8">
          <h2 className="text-lg font-semibold">Kurumsal entegrasyon</h2>
          <p className="mt-2 text-sm text-[var(--color-fg-muted)]">
            E-imzayı doğrudan kendi uygulamanıza ya da kurumsal altyapınıza entegre etmek istiyorsanız,
            ihtiyacınıza özel çözüm için <a href="/destek" className="text-[var(--color-accent)] hover:underline">bizimle iletişime geçin</a>.
          </p>
        </div>
      </section>
    </>
  )
}

function DownloadCard({ title, sub, primaryCommand, link, linkLabel, badge }: {
  title: string; sub: string; primaryCommand: string; link: string; linkLabel: string; badge?: string
}) {
  return (
    <div className="card">
      <div className="flex items-center gap-2">
        <h3 className="font-semibold">{title}</h3>
        {badge && (
          <span className="rounded-full bg-[var(--color-accent)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
            {badge}
          </span>
        )}
      </div>
      <p className="text-xs font-mono text-[var(--color-fg-dim)]">{sub}</p>
      <p className="mt-4 rounded bg-[var(--color-bg-soft)] p-3 text-sm font-medium">{primaryCommand}</p>
      <a href={link} target="_blank" rel="noopener" className="btn-ghost mt-4 text-xs inline-flex">
        {linkLabel} ↗
      </a>
    </div>
  )
}
